/**
 * Webtech HR Engine™ - Page d'Auto-Évaluation
 * Permet à l'employé de s'auto-évaluer sur un objectif
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import { Progress } from '../components/ui/progress';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Target,
  CheckCircle2,
  Loader2,
  ArrowLeft,
  Send,
  Star,
  AlertTriangle,
  Calendar,
  Weight
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import api from '../lib/api';

const SCORE_LABELS = {
  1: { label: 'Non atteint', description: 'L\'objectif n\'a pas été atteint', color: 'bg-red-100 text-red-700' },
  2: { label: 'Partiellement atteint', description: 'L\'objectif est partiellement atteint', color: 'bg-orange-100 text-orange-700' },
  3: { label: 'Atteint', description: 'L\'objectif est atteint comme prévu', color: 'bg-yellow-100 text-yellow-700' },
  4: { label: 'Bien atteint', description: 'L\'objectif est bien atteint avec qualité', color: 'bg-lime-100 text-lime-700' },
  5: { label: 'Dépassé', description: 'L\'objectif a été dépassé', color: 'bg-green-100 text-green-700' }
};

export default function SelfEvaluationPage() {
  const { objectiveId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);
  const [objective, setObjective] = useState(null);
  const [evaluation, setEvaluation] = useState(null); // For performance evaluation
  const [objectiveIndex, setObjectiveIndex] = useState(null); // Index in performance evaluation
  const [score, setScore] = useState(3);
  const [comment, setComment] = useState('');
  const [achievements, setAchievements] = useState('');
  const [challenges, setChallenges] = useState('');
  const [isPerformanceEval, setIsPerformanceEval] = useState(false);
  
  useEffect(() => {
    if (objectiveId) {
      loadObjective();
    }
  }, [objectiveId]);
  
  const loadObjective = async () => {
    try {
      // Check if this is a performance evaluation objective (format: {evaluationId}_obj_{index})
      if (objectiveId.includes('_obj_')) {
        const [evalId, , indexStr] = objectiveId.split('_obj_');
        const index = parseInt(indexStr) || parseInt(objectiveId.split('_').pop());
        
        // Load from performance evaluation system
        const response = await api.get(`/performance-eval/evaluations/${evalId}`);
        const evalData = response.data;
        
        if (evalData && evalData.objectives && evalData.objectives[index]) {
          const obj = evalData.objectives[index];
          setObjective({
            id: objectiveId,
            title: obj.title,
            description: obj.description,
            weight: obj.weight,
            indicators: obj.indicators,
            selfScore: obj.selfScore,
            selfComment: obj.selfComment
          });
          setEvaluation(evalData);
          setObjectiveIndex(index);
          setIsPerformanceEval(true);
          
          // Pre-fill if already partially evaluated
          if (obj.selfScore) {
            setScore(Math.round(obj.selfScore / 20)); // Convert 0-100 to 1-5
            setComment(obj.selfComment || '');
            setAchievements(obj.selfAchievements || '');
            setChallenges(obj.selfChallenges || '');
          }
        } else {
          throw new Error('Objectif non trouvé');
        }
      } else {
        // Load from old objectives system
        const response = await api.get(`/objectives/${objectiveId}`);
        setObjective(response.data);
        setIsPerformanceEval(false);
      }
    } catch (error) {
      console.error('Error loading objective:', error);
      toast.error('Impossible de charger l\'objectif');
      navigate('/my-evaluations');
    } finally {
      setLoading(false);
    }
  };
  
  const openConfirmSubmit = () => {
    if (!comment.trim()) {
      toast.error('Veuillez ajouter un commentaire sur votre performance');
      return;
    }
    setShowConfirmSubmit(true);
  };

  const handleSubmit = async () => {
    setShowConfirmSubmit(false);
    setSubmitting(true);
    try {
      if (isPerformanceEval && evaluation) {
        // Submit to performance evaluation system — 3 champs séparés
        await api.post(`/performance-eval/evaluations/${evaluation.id}/self-evaluate`, {
          objectiveIndex: objectiveIndex,
          score: score * 20, // Convert 1-5 to percentage (20-100)
          comment: comment,
          achievements: achievements,
          challenges: challenges
        });
      } else {
        // Submit to old evaluation system
        await api.post('/evaluation-workflow/self-evaluation', {
          objectiveId,
          score: score * 20,
          comment: comment,
          achievements: achievements,
          challenges: challenges
        });
      }
      
      toast.success('🎯 Auto-évaluation enregistrée avec succès !');
      navigate('/my-evaluations');
    } catch (error) {
      console.error('Error submitting evaluation:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <DashboardLayout title="Auto-évaluation">
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-webtech-blue" />
        </div>
      </DashboardLayout>
    );
  }
  
  if (!objective) {
    return (
      <DashboardLayout title="Auto-évaluation">
        <Card className="py-12">
          <CardContent className="text-center">
            <AlertTriangle className="h-16 w-16 mx-auto text-yellow-500 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Objectif non trouvé</h3>
            <p className="text-slate-500 mb-4">Cet objectif n'existe pas ou vous n'y avez pas accès.</p>
            <Button onClick={() => navigate('/my-evaluations')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour à mes évaluations
            </Button>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }
  
  const currentLabel = SCORE_LABELS[score];
  
  return (
    <DashboardLayout title="Auto-évaluation">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Button variant="ghost" onClick={() => navigate('/my-evaluations')} className="mb-2">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Target className="h-7 w-7 text-yellow-500" />
              Auto-évaluation
            </h1>
            <p className="text-slate-500 mt-1">Évaluez votre performance sur cet objectif</p>
          </div>
        </div>
        
        {/* Bandeau : auto-évaluation renvoyée par le manager */}
        {isPerformanceEval && evaluation?.returnedToSelf && evaluation?.selfEvalReturnReason && (
          <Card className="border-orange-300 bg-orange-50" data-testid="self-eval-returned-banner">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-orange-900">Votre manager vous demande de reprendre cette auto-évaluation</p>
                  <p className="text-sm text-orange-800 mt-1 whitespace-pre-wrap break-words">
                    <span className="font-medium">Motif :</span> {evaluation.selfEvalReturnReason}
                  </p>
                  <p className="text-xs text-orange-700 mt-2">
                    Vos réponses précédentes sont préservées — modifiez ce qui doit l'être, puis resoumettez.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Objective Card */}
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardHeader>
            <CardTitle className="text-lg text-blue-900">{objective.title}</CardTitle>
            <CardDescription className="text-blue-700">{objective.description}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Badge className="bg-blue-100 text-blue-700">
                <Weight className="w-3 h-3 mr-1" />
                Pondération: {objective.weight}%
              </Badge>
              {objective.endDate && (
                <Badge variant="outline" className="border-blue-300 text-blue-700">
                  <Calendar className="w-3 h-3 mr-1" />
                  Échéance: {new Date(objective.endDate).toLocaleDateString('fr-FR')}
                </Badge>
              )}
              <Badge variant="outline" className="border-blue-300 text-blue-700">
                {objective.type === 'individual' ? 'Individuel' : 
                 objective.type === 'team' ? 'Équipe' :
                 objective.type === 'enterprise' ? 'Entreprise' :
                 objective.type === 'department' ? 'Département' : 'Objectif'}
              </Badge>
            </div>
          </CardContent>
        </Card>
        
        {/* Evaluation Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              Votre évaluation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Score Slider */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <label className="text-sm font-medium text-slate-700">
                  Niveau d'atteinte de l'objectif
                </label>
                <Badge className={currentLabel.color}>
                  {score}/5 - {currentLabel.label}
                </Badge>
              </div>
              
              <Slider
                value={[score]}
                onValueChange={([v]) => setScore(v)}
                min={1}
                max={5}
                step={1}
                className="py-4"
              />
              
              <div className="flex justify-between text-xs text-slate-500">
                <span>Non atteint</span>
                <span>Partiellement</span>
                <span>Atteint</span>
                <span>Bien atteint</span>
                <span>Dépassé</span>
              </div>
              
              <div className={`p-3 rounded-lg ${currentLabel.color.replace('text-', 'bg-').replace('700', '50')}`}>
                <p className="text-sm text-slate-700">{currentLabel.description}</p>
                <p className="text-xs text-slate-500 mt-1">Score équivalent: {score * 20}%</p>
              </div>
            </div>
            
            {/* Comment */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Commentaire général sur votre performance *
              </label>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Décrivez comment vous avez travaillé sur cet objectif, les résultats obtenus..."
                rows={4}
                className="resize-none"
              />
            </div>
            
            {/* Achievements */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Principales réalisations (optionnel)
              </label>
              <Textarea
                value={achievements}
                onChange={(e) => setAchievements(e.target.value)}
                placeholder="Listez vos principales réalisations et succès..."
                rows={3}
                className="resize-none"
              />
            </div>
            
            {/* Challenges */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">
                Défis rencontrés (optionnel)
              </label>
              <Textarea
                value={challenges}
                onChange={(e) => setChallenges(e.target.value)}
                placeholder="Décrivez les difficultés rencontrées et comment vous les avez surmontées..."
                rows={3}
                className="resize-none"
              />
            </div>
          </CardContent>
        </Card>
        
        {/* Preview */}
        <Card className="bg-slate-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-600">Aperçu de votre évaluation</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-white border-4 border-yellow-400 flex items-center justify-center">
                <span className="text-2xl font-bold text-yellow-600">{score * 20}%</span>
              </div>
              <div>
                <p className="font-semibold text-slate-900">{currentLabel.label}</p>
                <p className="text-sm text-slate-500">Score pondéré: {(score * 20 * objective.weight / 100).toFixed(1)} points</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => navigate('/my-evaluations')}>
            Annuler
          </Button>
          <Button 
            onClick={openConfirmSubmit} 
            disabled={submitting || !comment.trim()}
            className="bg-green-600 hover:bg-green-700"
            data-testid="self-eval-submit-btn"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Envoi en cours...</>
            ) : (
              <><Send className="w-4 h-4 mr-2" />Soumettre mon auto-évaluation</>
            )}
          </Button>
        </div>
      </div>

      {/* Dialog de confirmation avant soumission */}
      <AlertDialog open={showConfirmSubmit} onOpenChange={setShowConfirmSubmit}>
        <AlertDialogContent data-testid="confirm-self-eval-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer l'auto-évaluation</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-slate-600">
                <p>Vous vous apprêtez à soumettre votre auto-évaluation pour cet objectif.</p>
                <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Note attribuée</span>
                    <span className="font-semibold text-slate-900">
                      {score}/5 · {score * 20}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Niveau</span>
                    <span className="font-medium text-slate-900">{currentLabel.label}</span>
                  </div>
                </div>
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  ⚠ Une fois soumise, votre auto-évaluation sera transmise à votre manager.
                  Vous pourrez la modifier uniquement si votre manager vous la renvoie.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-self-eval-cancel">
              Annuler, je veux relire
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-self-eval-confirm"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Envoi...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" />Confirmer l'auto-évaluation</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
